import apiService from './api';
import { storage } from '../utils/storage';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.isInitialized = false;
  }

  async register(userData) {
    try {
      const response = await apiService.auth.register(userData);
      
      if (response.success && response.data) {
        const { user, token } = response.data;
        
        localStorage.setItem('medigo_token', token);
        storage.setUser(user);
        this.currentUser = user;
        
        return {
          success: true,
          data: { user },
          message: response.message
        };
      }
      
      return {
        success: false,
        message: response.message || 'Registration failed'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Registration failed'
      };
    }
  }

  async login(credentials) {
    try {
      const response = await apiService.auth.login(credentials);
      
      if (response.success && response.data) {
        const { user, token } = response.data;
        
        if (!user || !token) {
          return {
            success: false,
            message: 'Invalid response from server'
          };
        }
        
        localStorage.setItem('medigo_token', token);
        storage.setUser(user);
        this.currentUser = user;
        
        return {
          success: true,
          data: { user },
          message: response.message || 'Login successful'
        };
      }
      
      return {
        success: false,
        message: response.message || 'Login failed'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Login failed'
      };
    }
  }

  async logout() {
    try {
      await apiService.auth.logout();
    } catch (error) {
      // Silent fail
    } finally {
      localStorage.removeItem('medigo_token');
      storage.clearAuth();
      this.currentUser = null;
      this.isInitialized = false;
    }
  }

  async getCurrentUser() {
    try {
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        return {
          success: false,
          message: 'No token found'
        };
      }
      
      const response = await apiService.auth.getCurrentUser();
      
      if (response.success && response.data) {
        const user = response.data;
        storage.setUser(user);
        this.currentUser = user;
        this.isInitialized = true;
        
        return {
          success: true,
          data: user
        };
      }
      
      localStorage.removeItem('medigo_token');
      storage.clearAuth();
      this.currentUser = null;
      
      return {
        success: false,
        message: 'Not authenticated'
      };
    } catch (error) {
      localStorage.removeItem('medigo_token');
      storage.clearAuth();
      this.currentUser = null;
      
      return {
        success: false,
        message: error.message || 'Failed to get user'
      };
    }
  }

  async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiService.auth.changePassword({
        currentPassword,
        newPassword
      });
      
      return {
        success: response.success,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Password change failed'
      };
    }
  }

  async forgotPassword(data) {
    try {
      const response = await apiService.auth.forgotPassword(data);
      
      return {
        success: response.success,
        message: response.message,
        emailSent: response.emailSent,
        emailServiceError: response.emailServiceError,
        otp: response.otp
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send reset instructions'
      };
    }
  }

  async verifyOTP(data) {
    try {
      const response = await apiService.auth.verifyOTP(data);
      const resetToken = response.resetToken || response.data?.resetToken;

      return {
        success: response.success,
        message: response.message,
        resetToken: resetToken,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'OTP verification failed'
      };
    }
  }

  async resetPassword(data) {
    try {
      const response = await apiService.auth.resetPassword(data);
      
      return {
        success: response.success,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Password reset failed'
      };
    }
  }

  async updateProfile(userData) {
    try {
      const response = await apiService.users.updateProfile(userData);
      
      if (response.success && response.data) {
        const user = response.data;
        storage.setUser(user);
        this.currentUser = user;
        
        return {
          success: true,
          data: user,
          message: response.message
        };
      }
      
      return {
        success: false,
        message: response.message || 'Profile update failed'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Profile update failed'
      };
    }
  }

  async verify(data) {
    try {
      const response = await apiService.auth.verifyEmail(data.otp);
      
      if (response.success && response.data) {
        const user = response.data;
        storage.setUser(user);
        this.currentUser = user;
        
        return {
          success: true,
          data: user,
          message: response.message
        };
      }
      
      return {
        success: false,
        message: response.message || 'Verification failed'
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Verification failed'
      };
    }
  }

  async sendVerification(data) {
    try {
      const response = await apiService.auth.resendVerification(data);
      
      return {
        success: response.success,
        message: response.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send verification code'
      };
    }
  }

  getCachedUser() {
    if (!this.currentUser) {
      this.currentUser = storage.getUser();
    }
    return this.currentUser;
  }

  isAuthenticated() {
    const token = localStorage.getItem('medigo_token');
    return !!token && !!this.getCachedUser();
  }

  getToken() {
    return localStorage.getItem('medigo_token');
  }

  hasRole(role) {
    const user = this.getCachedUser();
    return user && user.role === role;
  }

  hasAnyRole(roles) {
    const user = this.getCachedUser();
    return user && roles.includes(user.role);
  }

  hasPermission(permission) {
    const user = this.getCachedUser();
    if (!user) return false;

    const rolePermissions = {
      admin: ['*'],
      hospital: [
        'doctors.view', 'doctors.create', 'doctors.update',
        'ambulances.view', 'ambulances.create', 'ambulances.update',
        'appointments.view', 'appointments.update',
        'patients.view', 'inventory.view', 'inventory.update'
      ],
      doctor: [
        'patients.view', 'consultations.view', 'consultations.update',
        'prescriptions.create', 'prescriptions.update',
        'schedule.view', 'schedule.update'
      ],
      pharmacy: [
        'orders.view', 'orders.update', 'inventory.view', 'inventory.update',
        'prescriptions.view', 'delivery.manage'
      ],
      driver: [
        'trips.view', 'trips.update', 'location.update',
        'ambulance.operate'
      ],
      patient: [
        'consultations.create', 'consultations.view',
        'orders.create', 'orders.view',
        'prescriptions.view', 'profile.update'
      ]
    };

    const userPermissions = rolePermissions[user.role] || [];
    
    if (userPermissions.includes('*')) return true;
    
    return userPermissions.includes(permission);
  }

  getUserPermissions() {
    const user = this.getCachedUser();
    if (!user) return [];

    const rolePermissions = {
      admin: ['*'],
      hospital: [
        'doctors.manage', 'ambulances.manage', 'appointments.manage',
        'patients.view', 'inventory.manage'
      ],
      doctor: [
        'patients.view', 'consultations.manage', 'prescriptions.manage',
        'schedule.manage'
      ],
      pharmacy: [
        'orders.manage', 'inventory.manage', 'prescriptions.view',
        'delivery.manage'
      ],
      driver: [
        'trips.manage', 'location.update', 'ambulance.operate'
      ],
      patient: [
        'consultations.create', 'orders.create', 'prescriptions.view',
        'profile.manage'
      ]
    };

    return rolePermissions[user.role] || [];
  }

  isAccountVerified() {
    const user = this.getCachedUser();
    return user && user.isVerified;
  }

  hasActiveSubscription() {
    const user = this.getCachedUser();
    if (!user || !user.subscriptionExpiry) return false;
    
    const expiryDate = new Date(user.subscriptionExpiry);
    return expiryDate > new Date();
  }

  getSubscriptionStatus() {
    const user = this.getCachedUser();
    if (!user) return 'none';
    if (!user.subscription) return 'none';
    if (this.hasActiveSubscription()) return 'active';
    return 'expired';
  }

  updateUserData(userData) {
    if (this.currentUser) {
      this.currentUser = { ...this.currentUser, ...userData };
      storage.setUser(this.currentUser);
    }
  }

  clearAuthData() {
    localStorage.removeItem('medigo_token');
    storage.clearAuth();
    this.currentUser = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return this.currentUser;
    
    try {
      const result = await this.getCurrentUser();
      this.isInitialized = true;
      return result.success ? result.data : null;
    } catch (error) {
      this.isInitialized = true;
      return null;
    }
  }
}

const authService = new AuthService();

export { authService };